import React, { RefObject, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Button } from 'new/elements/Button';
import moment from 'moment/moment';
import cx from 'classnames';
import { DateRangePicker } from 'new/elements/DateRangePicker';
import FormatUtils from 'js/lib/FormatUtils';
import { useAccountMetrics } from 'new/hooks/adapters/useAccountMetrics';
import { fetchOrgBillableEvents, fetchUserBillableEvents } from 'js/actions';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ReactComponent as ArrowRight } from 'new/components/icons/arrowRight.svg';
import styles from 'new/components/monitor/Utilization/Utilization.module.scss';
import { DataUsage } from './DataUsage/DataUsage';

interface MonthlyAccountUsageProps {
  rootClassName?: string;
  titleHoverRef?: RefObject<HTMLElement>;
  dateRange?: { startDate: Date; endDate: Date };
  onDatesChanged?: (startDate: Date, endDate: Date) => void;
  onSeeAllDetails?: (dates: { startDate: Date; endDate: Date }) => void;
}

const sDate = moment().startOf('month').toDate();
const eDate = moment().toDate();

export const MonthlyAccountUsage = (props: MonthlyAccountUsageProps) => {
  const { userData, orgData, isLoading } = useAccountMetrics();
  const dispatch = useAppDispatch();
  const user = useCurrentUser();
  const org = useCurrentOrg();
  const isOrgView = user.isAdmin;
  const [localDates, setLocalDates] = useState({ startDate: sDate, endDate: eDate });
  const dates = props.dateRange ?? localDates;

  const onDatesChanged = (startDate, endDate) => {
    if (props.onDatesChanged) {
      props.onDatesChanged(startDate, endDate);
    } else {
      setLocalDates({ startDate, endDate });
    }
  };

  useEffect(() => {
    if (user.isAdmin) {
      dispatch(
        fetchOrgBillableEvents({
          orgId: org.id,
          startDate: dates.startDate,
          endDate: dates.endDate,
        }),
      );
      dispatch(
        fetchUserBillableEvents({
          userId: user.id,
          startDate: dates.startDate,
          endDate: dates.endDate,
          orgId: org.id,
        }),
      );
    } else {
      dispatch(
        fetchUserBillableEvents({
          userId: user.id,
          startDate: dates.startDate,
          endDate: dates.endDate,
          orgId: org.id,
        }),
      );
    }
  }, [user.isAdmin, user.id, org.id, dates.startDate, dates.endDate, dispatch]);

  return (
    <div className={cx(styles.usageContainer, props.rootClassName)}>
      <div className={styles.usageHeader}>
        <div className={styles.usageHeaderLeft}>
          <Typography variant="subtitle1" className={styles.usageTitle}>
            Account Usage
          </Typography>
          <DateRangePicker
            startDate={dates.startDate}
            endDate={dates.endDate}
            onChange={onDatesChanged}
            maxDate={new Date()}
          />
        </div>
        <div className={styles.usageHeaderRight}>
          {props.onSeeAllDetails && (
            <Button
              variant="text"
              size="small"
              endIcon={<ArrowRight />}
              onClick={() => props.onSeeAllDetails?.(dates)}
              className={styles.seeAllButton}
            >
              See all details
            </Button>
          )}
        </div>
      </div>
      <DataUsage
        isOrgView={isOrgView}
        isLoading={isLoading}
        dataVolume={{
          org: FormatUtils.bytesToString(orgData?.data?.size),
          user: FormatUtils.bytesToString(userData?.data?.size),
        }}
        numberOfRecords={{
          org: FormatUtils.formatNamedNumber(orgData?.data?.records),
          user: FormatUtils.formatNamedNumber(userData?.data?.records),
        }}
      />
    </div>
  );
};
